import { ReactElement, useEffect, useState } from "react";
import { IContentAreaProps, IFilterData, IState } from "../../../../types";
import FilterComponentBase from "../../../../components/filterComponentBase";
import { DateTime } from "luxon";
import { IBilletTons, ICrewDetailReport, IDelayInfo } from "./types";
import { Alert } from "@mui/material";
import {
  IBarChartConfig,
  IPieConfig,
  IPoint,
  ISeries,
  ISeriesWithAxes,
  IStackedBarChartConfig
} from "../../../../components/charts/types";
import { formatNumber, roundUp } from "../../../../system/utils";
import BarChart from "../../../../components/charts/barChart";
import PieChart from "../../../../components/charts/pieChart";
import BilletTonsTable from "./components/billetTonsTable";
import AvgBilletTonsTable from "./components/avgBilletTonsTable";
import { useSelector } from "react-redux";
import { useFetch } from "../../../../system/useFetch";
import StackedBarChart from "../../../../components/charts/stackedBarChart";
import SkeletonView from "../../../../components/skeletonView";

interface ICharts {
  billetTon: IBarChartConfig;
  delays: IStackedBarChartConfig;
  pieConfig: IPieConfig;
  table: IBilletTons[];
}

const CrewDetail = ({ parentSize }: IContentAreaProps): ReactElement => {
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const { loading, error, data } = useFetch<ICrewDetailReport>({ module: "meltShop", query: "getCrewDetailReport", payload: { ...filter.remote } });
  const [chartsConfig, setChartsConfig] = useState<ICharts | null>(null);

  useEffect(() => {
    if (data) {

      const seriesA: ISeriesWithAxes[] = [];
      const seriesB: ISeriesWithAxes[] = [];
      const seriesC: ISeries[] = [];
      const categoriesA: string[] = [];
      const categoriesB: string[] = [];

      let maximumA = 0;
      let maximumB = 0;
      const hours: any = {};

      (data.billetTons as IBilletTons[]).forEach((row: IBilletTons) => {
        let item = seriesA.find(x => x.name === row.crewName);
        if (!item) {
          item = {
            name: row.crewName,
            xAxisName: "Day",
            yAxisName: "Billet Tons",
            value: [],
            color: row.crewColor,
            unit: "US ton"
          };

          seriesA.push(item);

          seriesC.push({
            name: row.crewName,
            value: 0,
            color: row.crewColor,
            unit: "US ton"
          });
        }

        (item.value as IPoint[]).push({ x: `${row.factoryDay}`, y: row.total });

        maximumA = Math.max(maximumA, row.total);

        if (!categoriesA.find(x => x === `${row.factoryDay}`)) {
          categoriesA.push(`${row.factoryDay}`);
        }

        const itemC = seriesC.find(x => x.name === row.crewName);
        if(itemC) {
          (itemC.value as number) += row.total / 1000;
        }

      });

      (data.delays as IDelayInfo[]).forEach((row: IDelayInfo) => {
        let item = seriesB.find(x => x.name === row.category);
        if (!item) {
          item = {
            name: row.category,
            xAxisName: "Crew",
            yAxisName: "Hours",
            value: [],
            color: row.categoryColor,
            unit: "h"
          };

          seriesB.push(item);
        }

        (item.value as IPoint[]).push({ x: row.crewName, y: row.hours });

        if (!hours[row.crewName]) {
          hours[row.crewName] = row.hours;
        } else {
          hours[row.crewName] += row.hours;
        }

        if (!categoriesB.find(x => x === row.crewName)) {
          categoriesB.push(row.crewName);
        }
      });

      Object.keys(hours).forEach(key => {
        maximumB = Math.max(maximumB, hours[key]);
      });

      setChartsConfig({
        delays: {
          xAxis: {
            name: "Crew",
            categories: categoriesB
          },
          yAxis: {
            name: "Hours"
          },
          title: "Hours by CREW and Responsibility",
          series: seriesB,
          showLegend: true
        },

        billetTon: {
          xAxis: {
            name: "Day",
            categories: categoriesA,
            formatTick: (value) => {
              const dt = parseInt(value as string, 10);

              return `${DateTime.fromSeconds(dt).day}`;
            }
          },
          yAxis: [{
            name: "Billet Tons",
            minimum: 0,
            maximum: roundUp(maximumA, 1),
            ticksCount: 3,
            formatTick: (value) => {
              return typeof value === "number" ? (value as number).toFixed(2) : (value as IPoint).y.toFixed(2);
            }
          }],
          title: "Billet Tons by Day and Crew",
          series: seriesA,
          multiple: true,
          showLegend: true
        },

        pieConfig: {
          title: "Billet Tons by Crew",
          maximum: 0,
          radius: 0.9,
          padAngle: 0.02,
          innerRadius: 0.05,
          seriesRadius: 1.1,
          series: seriesC,
          showPercent: true,
          formatValue: (n: number) => `${formatNumber(n, 1)}K`
        },

        table: data.billetTons as IBilletTons[]
      });
    }
  }, [data]);

  const layoutStyle = {
    ...parentSize,
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: "1fr",
  };

  const subLayoutStyle = {
    gridTemplateRows: "1fr 1fr",
    gridTemplateColumns: "30% 1fr 1fr 1fr",
    padding: "1em"
  };

  const tableAreaStyle = {
    gridRow: "1 / span 2",
    overflow: "scroll",
  };

  const chart3AreaStyle = {
    gridColumn: "2 / span 3"
  };

  const isMobile = parentSize.width < 400 || parentSize.height < 400;

  return (
    <div className="display-grid" style={layoutStyle}>
      <FilterComponentBase loading={loading} />

      {loading && <SkeletonView  parentSize={parentSize}/>}
      {error && <Alert severity="warning">{error.message}</Alert>}
      {!loading && !error && !chartsConfig && <Alert severity="warning">No Data to Display.</Alert>}

      {!loading && !error && chartsConfig && !isMobile &&
      <div className="display-grid" style={subLayoutStyle}>

        <div style={tableAreaStyle}>
          {chartsConfig &&  <BilletTonsTable columns={chartsConfig.delays.xAxis.categories ?? []} data={chartsConfig.table} parentSize={parentSize}/>}
        </div>

        <div style={{ margin: "0em 2em" }}>
          {chartsConfig &&  <AvgBilletTonsTable data={chartsConfig.table} />}
        </div>

        <div>
          {chartsConfig && <PieChart pieConfig={chartsConfig.pieConfig}/>}
        </div>

        <div>
          {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delays}/>}
        </div>

        <div style={chart3AreaStyle}>
          {chartsConfig && <BarChart barChartConfig={chartsConfig?.billetTon}/>}
        </div>

      </div>
      }

      {!loading && !error && chartsConfig && isMobile &&
      <div className="p-1" style={{ width: parentSize.width - 16 }}>
        {chartsConfig &&  <BilletTonsTable columns={chartsConfig.delays.xAxis.categories ?? []} data={chartsConfig.table} parentSize={parentSize}/>}
        {chartsConfig &&  <AvgBilletTonsTable data={chartsConfig.table} />}

        <div style={{ height: 250 }}>
          {chartsConfig && <PieChart pieConfig={chartsConfig.pieConfig}/>}
        </div>
        <div style={{ height: 250 }}>
          {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delays}/>}
        </div>
        <div style={{ height: 250 }}>
          {chartsConfig && <BarChart barChartConfig={chartsConfig?.billetTon}/>}
        </div>
      </div>
      }
    </div>
  );
};

export default CrewDetail;
